import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import HostedPage from './index'
import usePaymentSessionTimeoutFromAPI from '../../hooks/usePaymentSessionTimeoutFromAPI'
import useIdleTimeout from '../../hooks/useIdleTimeout'
import IdleTimeoutModal from '../idle-timeout-modal'
import SessionTimeoutScreen from '../session-timeout-screen'
import TimeoutDebugPanel from '../timeout-debug-panel'
import DebugCogToggle from '../debug-cog-toggle'
import TimeoutCountdownBar from '../timeout-countdown-bar'
import { getSessionTimeoutConfig, validateTimeoutConfig } from '../../utils/session-timeout-config'
import messageService from '../../utils/message-service'

/**
 * Wrapper component that adds idle timeout functionality to the SDK page
 */
const SdkPageWithTimeout = props => {
    const { id: paymentSessionId } = useParams()
    const [timeoutConfig, setTimeoutConfig] = useState(null)
    const [isConfigLoaded, setIsConfigLoaded] = useState(false)
    const [showTimeoutScreen, setShowTimeoutScreen] = useState(false)
    const [showDebugPanel, setShowDebugPanel] = useState(false)
    const [windowOpened, setWindowOpened] = useState(false)

    // Load timeout configuration on mount
    useEffect(() => {
        const loadConfig = async () => {
            try {
                const config = await getSessionTimeoutConfig()
                const validatedConfig = validateTimeoutConfig(config)
                setTimeoutConfig(validatedConfig)
                setIsConfigLoaded(true)
                setWindowOpened(window.opener != null)
            } catch (error) {
                console.error('Failed to load timeout configuration:', error)
                setIsConfigLoaded(true) // Still set to true to prevent infinite loading
            }
        }

        loadConfig()
    }, [])

    // Handle timeout events
    const handleTimeout = () => {
        // Close any open payment modals
        if (window.DataMesh && typeof window.DataMesh.destroy === 'function') {
            try {
                window.DataMesh.destroy()
            } catch (e) {
                console.error('Error destroying DataMesh SDK on timeout:', e)
            }
        }

        // In test environment, redirect immediately
        if (process.env.NODE_ENV === 'test') {
            const redirectUrl = timeoutConfig?.redirectUrl || timeoutConfig?.logoutUrl || window.location.origin
            window.location.href = redirectUrl
            return
        }

        // Show timeout screen instead of redirecting immediately
        setShowTimeoutScreen(true)
    }

    const handleWarning = () => {
        // Send timeout warning message using the message service
        messageService.sendTimeoutWarning(remainingTime, {
            sessionDuration: Date.now() - sessionStartTime,
        })
    }

    const handleExtendSession = () => {
        extendSession()
    }

    const handleLogout = () => {
        // Close any open payment modals
        if (window.DataMesh && typeof window.DataMesh.destroy === 'function') {
            try {
                window.DataMesh.destroy()
            } catch (e) {
                console.error('Error destroying DataMesh SDK on logout:', e)
            }
        }

        // In test environment, redirect immediately
        if (process.env.NODE_ENV === 'test') {
            const redirectUrl = timeoutConfig?.redirectUrl || timeoutConfig?.logoutUrl || window.location.origin
            window.location.href = redirectUrl
            return
        }

        // Show timeout screen for manual logout too
        setShowTimeoutScreen(true)
    }

    // Handle closing the timeout screen (window close)
    const handleTimeoutScreenClose = () => {
        // Send final message using the message service
        messageService.sendWindowClosing('Session timeout - window closing', {
            sessionDuration: Date.now() - sessionStartTime,
        })

        // Close the window or redirect
        if (windowOpened) {
            // If opened as popup, close the window
            window.close()
        } else {
            // If in iframe or direct navigation, redirect
            const redirectUrl = timeoutConfig?.redirectUrl || timeoutConfig?.logoutUrl || window.location.origin
            window.location.href = redirectUrl
        }
    }

    // Track session start time
    const [sessionStartTime] = useState(Date.now())

    // Use API-based timeout if payment session ID is available
    const apiTimeoutHook = usePaymentSessionTimeoutFromAPI(
        timeoutConfig && timeoutConfig.enabled && paymentSessionId
            ? {
                  paymentSessionId,
                  inactivityTimeout: 15 * 60 * 1000, // 15 minutes
                  maxWarnings: 4,
                  onTimeout: handleTimeout,
                  onInactivityWarning: handleWarning,
                  events: timeoutConfig.events,
                  enabled: true,
              }
            : {
                  enabled: false,
              },
    )

    // Use config-based timeout as fallback when no payment session ID
    const configTimeoutHook = useIdleTimeout(
        timeoutConfig && timeoutConfig.enabled && !paymentSessionId
            ? {
                  timeout: timeoutConfig.timeout,
                  warningTime: timeoutConfig.warningTime,
                  onTimeout: handleTimeout,
                  onWarning: handleWarning,
                  events: timeoutConfig.events,
                  enabled: true,
              }
            : {
                  enabled: false,
              },
    )

    // Choose which hook to use based on whether we have a payment session ID
    const activeHook = paymentSessionId ? apiTimeoutHook : configTimeoutHook

    const {
        remainingTime,
        sessionDuration,
        isSessionExpired,
        isInactivityWarning,
        extendSession,
        triggerTimeout,
        triggerInactivityWarning,
        getRemainingTimeFormatted,
        // Config timeout hook uses different property names
        isIdle: configIsIdle,
        isWarning: configIsWarning,
        triggerWarning: configTriggerWarning,
    } = activeHook

    // Normalize the different property names between hooks
    const isWarning = isInactivityWarning || configIsWarning
    const isIdle = isSessionExpired || configIsIdle
    const triggerWarning = triggerInactivityWarning || configTriggerWarning

    // Check if debug mode is enabled (development or query parameter)
    const isDebugModeEnabled =
        process.env.NODE_ENV === 'development' ||
        new URLSearchParams(window.location.search).has('debug') ||
        timeoutConfig?.debug === true

    // Track if debug panel was manually closed
    const [debugPanelManuallyClosed, setDebugPanelManuallyClosed] = useState(false)

    // Show debug panel if debug mode is enabled and panel hasn't been manually closed
    useEffect(() => {
        if (isDebugModeEnabled && !debugPanelManuallyClosed) {
            setShowDebugPanel(false)
        }
    }, [isDebugModeEnabled, debugPanelManuallyClosed])

    const handleCloseDebugPanel = () => {
        setShowDebugPanel(false)
        setDebugPanelManuallyClosed(true)
    }

    const handleToggleDebugPanel = () => {
        setShowDebugPanel(!showDebugPanel)
        // Reset the manually closed flag when toggling via cog
        if (!showDebugPanel) {
            setDebugPanelManuallyClosed(false)
        }
    }

    // Add/remove body class for countdown bar spacing
    useEffect(() => {
        if (timeoutConfig && timeoutConfig.enabled && !showTimeoutScreen) {
            document.body.classList.add('timeout-bar-visible')
        } else {
            document.body.classList.remove('timeout-bar-visible')
        }

        // Cleanup on unmount
        return () => {
            document.body.classList.remove('timeout-bar-visible')
        }
    }, [timeoutConfig, showTimeoutScreen])

    // Don't render anything until config is loaded
    if (!isConfigLoaded) {
        return <div>Loading...</div>
    }

    // If timeout is disabled, just render the original component
    if (!timeoutConfig || !timeoutConfig.enabled) {
        return <HostedPage {...props} />
    }

    return (
        <>
            <TimeoutCountdownBar
                remainingTime={remainingTime}
                totalTime={sessionDuration || timeoutConfig.timeout}
                isVisible={!showTimeoutScreen && remainingTime > 0}
                isWarning={isWarning}
                onExtendSession={handleExtendSession}
                config={{
                    primaryColor: timeoutConfig.modal?.primaryColor,
                    normalColor: '#28a745',
                    warningColor: '#ffc107',
                    criticalColor: '#dc3545',
                    normalText:
                        paymentSessionId && !apiTimeoutHook.error
                            ? 'Payment session expires at'
                            : 'Payment session time remaining',
                    warningText: 'Payment session expiring soon!',
                    extendButtonText: timeoutConfig.modal?.extendButtonText || 'Extend Session',
                }}
            />
            <HostedPage {...props} />
            <IdleTimeoutModal
                isVisible={isWarning && !showTimeoutScreen}
                remainingTime={getRemainingTimeFormatted()}
                onExtendSession={handleExtendSession}
                onLogout={handleLogout}
                config={timeoutConfig.modal}
            />
            <SessionTimeoutScreen
                isVisible={showTimeoutScreen}
                onClose={handleTimeoutScreenClose}
                config={{
                    ...timeoutConfig.modal,
                    title: timeoutConfig.timeoutScreen?.title || 'Session Expired',
                    message:
                        timeoutConfig.timeoutScreen?.message ||
                        'Your session has expired due to inactivity. This window will close automatically.',
                    countdownMessage: timeoutConfig.timeoutScreen?.countdownMessage || 'Closing in {countdown} seconds...',
                    autoCloseDelay: timeoutConfig.timeoutScreen?.autoCloseDelay || 5000,
                }}
            />
            <TimeoutDebugPanel
                isVisible={showDebugPanel}
                onTriggerWarning={triggerWarning}
                onTriggerTimeout={triggerTimeout}
                onExtendSession={handleExtendSession}
                onClose={handleCloseDebugPanel}
                remainingTime={remainingTime}
                isWarning={isWarning}
                isIdle={isIdle || showTimeoutScreen}
            />
            <DebugCogToggle
                isVisible={isDebugModeEnabled}
                onClick={handleToggleDebugPanel}
                isDebugPanelVisible={showDebugPanel}
            />
        </>
    )
}

export default SdkPageWithTimeout
